"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type AudioRecorderState = "idle" | "requesting" | "recording" | "stopped";

export interface UseAudioRecorderReturn {
  state: AudioRecorderState;
  durationMs: number;
  audioBlob: Blob | null;
  audioMimeType: string;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  reset: () => void;
}

// Ordered by preference — first supported wins.
const PREFERRED_MIME_TYPES = [
  "audio/webm; codecs=opus",
  "audio/webm",
  "audio/mp4; codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg; codecs=opus",
];

function detectMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  return PREFERRED_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

// Module-level singleton — iOS Safari allows max 4 AudioContext instances per page.
let _audioCtx: AudioContext | null = null;

async function unlockAudioContext(): Promise<void> {
  if (typeof AudioContext === "undefined") return;
  if (!_audioCtx) _audioCtx = new AudioContext();
  if (_audioCtx.state === "suspended") await _audioCtx.resume();
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [state, setState] = useState<AudioRecorderState>("idle");
  const [durationMs, setDurationMs] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  // Start empty to avoid SSR/client hydration mismatch; set on mount.
  const [audioMimeType, setAudioMimeType] = useState<string>("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCancelledRef = useRef(false);

  useEffect(() => {
    setAudioMimeType(detectMimeType());
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    setState("requesting");
    isCancelledRef.current = false;
    try {
      await unlockAudioContext();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const options = audioMimeType ? { mimeType: audioMimeType } : undefined;
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        if (isCancelledRef.current) return;
        const blob = new Blob(chunksRef.current, {
          type: audioMimeType || "audio/webm",
        });
        setAudioBlob(blob);
        setState("stopped");
      };

      recorder.start(250); // collect chunk every 250ms
      setState("recording");

      setDurationMs(0);
      timerRef.current = setInterval(() => {
        setDurationMs((ms) => ms + 1000);
      }, 1000);
    } catch (err: unknown) {
      setState("idle");
      if (err instanceof DOMException) {
        if (err.name === "NotAllowedError") {
          throw new Error("Microphone permission denied");
        }
        if (err.name === "NotFoundError") {
          throw new Error("No microphone found");
        }
      }
      throw err;
    }
  }, [audioMimeType]);

  const stopRecording = useCallback(() => {
    clearTimer();
    mediaRecorderRef.current?.stop();
    stopStream();
    // state transitions to 'stopped' via recorder.onstop
  }, [clearTimer, stopStream]);

  const cancelRecording = useCallback(() => {
    clearTimer();
    isCancelledRef.current = true;
    chunksRef.current = [];
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current?.stop();
    }
    stopStream();
    setAudioBlob(null);
    setDurationMs(0);
    setState("idle");
  }, [clearTimer, stopStream]);

  const reset = useCallback(() => {
    clearTimer();
    stopStream();
    setAudioBlob(null);
    setDurationMs(0);
    setState("idle");
  }, [clearTimer, stopStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
      stopStream();
      isCancelledRef.current = true;
      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current?.stop();
      }
    };
  }, [clearTimer, stopStream]);

  return {
    state,
    durationMs,
    audioBlob,
    audioMimeType,
    startRecording,
    stopRecording,
    cancelRecording,
    reset,
  };
}
